import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
} from "@mui/material";
import ResponsiveAppBar from "../components/ResponsiveAppBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SignUpForm from "../sections/SignupForm"; // Adjust the import path as needed
import { IconButton, List, ListItem, ListItemIcon, ListItemText, Modal } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Book } from "../types/book";
import { bookPlatforms } from "../sections/HomeSection";


const ShardSeriesBook1: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [openPreorderModal, setOpenPreorder] = useState(false);

  // Access the bookItem from the location state
  const bookItem = location.state?.bookItem as Book | undefined;

  // Fallback to URL parameter if state is not available
  const bookTitle = bookItem
    ? bookItem.title
    : params.title
    ? decodeURIComponent(params.title!.replace(/-/g, " "))
    : "Unknown Book";

  // Use description from the bookItem if available, otherwise provide a fallback
  const bookDescription = bookItem
    ? bookItem.description
    : "Description not available.";

  const handleNavClick = (id: string): void => {
    navigate(`/#${id}`);
  };

  const handleBack = () => {
    handleNavClick("books");
  };

  const handleClose = () => {
    setOpenPreorder(false);
  }
  const handlePlatformClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Mock books data (replace with actual data source)
  const books = [
    {
      title: bookTitle,
      downloadLink: bookItem?.downloadLink || "", // Adjust based on your Book type
    },
  ];
  const currentBookIndex = 0; // Adjust based on your logic


  // Handle case where neither state nor URL parameter is available
  if (!bookItem && !params.title) {
    return (
      <>
        <ResponsiveAppBar onNavClick={handleNavClick} />
        <Container
          maxWidth="lg"
          sx={{
            mt: 10,
            mb: 4,
            py: 4,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            backgroundColor: "#f0f0f0",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, mb: 4, textAlign: "center" }}>
            <Typography variant="h5" gutterBottom>
              Book Not Found
            </Typography>
            <Typography variant="body1">
              The details for this book could not be loaded. Please try again from
              the book list.
            </Typography>
            <Button
              variant="contained"
              sx={{
                marginRight: { xs: 0, sm: '5%' },
                padding: { xs: '15px 30px', md: '20px 48px' },
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                width:'300px',
                height: '100%',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '2px solid #55DEF1',
                color: '#55DEF1',
                '&:hover': {
                  background: 'rgba(85, 222, 241, 0.2)',
                  backdropFilter: 'blur(15px)',
                  border: '2px solid #55DEF1',
                }
              }}
              startIcon={<ArrowBackIcon />}
              size="large"
              onClick={handleBack}
            >
              Back to All Books
            </Button>
          </Paper>
        </Container>
      </>
    );
  }

  // Main render when book details are available
  return (
    <>
      <ResponsiveAppBar onNavClick={handleNavClick} />
      <Container
        maxWidth="lg"
        sx={{
          mt: 10,
          mb: 4,
          py: 4,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          backgroundColor: "#00000000",
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "8px",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(15px)",
            WebkitBackdropFilter: "blur(15px)",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
            overflow: "hidden",
          }}
        >
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 300, md: 350 },
                  bgcolor: "#00000000",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "4px",
                  overflow: "hidden",
                  backgroundImage: bookItem?.coverImageUrl
                    ? `url(${bookItem.coverImageUrl})`
                    : "none",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {!bookItem?.coverImageUrl && (
                  <Typography variant="body1" color="text.secondary">
                    Book Cover Placeholder
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography
                variant="h2" // Increased from h3 to h2
                component="h1"
                gutterBottom
              >
                {bookTitle}
              </Typography>
              <Typography
                variant="h5" // Increased from subtitle1 to h6
                color="text.secondary"
                gutterBottom
              >
                By Martin Knaggs
              </Typography>
              <Box sx={{ my: 3 }}>
                <Typography variant="h4" gutterBottom>
                  About this book
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ 
                    lineHeight: 1.7, 
                    whiteSpace: "pre-wrap", 
                    fontSize: "1.2rem",
                    textAlign: "left",  // Explicitly set left alignment
                    margin: 0,          // Remove any default margins
                    padding: 0          // Remove any default padding
                  }}
                >
                  {bookDescription.trim()}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    marginRight: { xs: 0, sm: '5%' },
                    padding: { xs: '15px 30px', md: '20px 48px' },
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                    width:'100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid #55DEF1',
                    color: '#000000ff',
                    '&:hover': {
                      background: 'rgba(85, 222, 241, 0.2)',
                      backdropFilter: 'blur(15px)',
                      border: '2px solid #55DEF1',
                    }
                  }}
                  startIcon={<ArrowBackIcon />}
                  size="large"
                  onClick={handleBack}
                >
                  Back to All Books
                </Button>
                {books[currentBookIndex].downloadLink && (
                  <Button
                    variant="contained"
                    sx={{
                      marginRight: { xs: 0, sm: '5%' },
                      padding: { xs: '15px 30px', md: '20px 48px' },
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width:'100%',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid #55DEF1',
                      color: '#000000ff',
                      '&:hover': {
                        background: 'rgba(85, 222, 241, 0.2)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid #55DEF1',
                      }
                    }}
                    size="large"
                    onClick={()=> {
                      setOpenPreorder(true);
                    }}
                  >
                    ORDER NOW
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
        {/* The Factions section */}
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "8px",
            mt: { xs: 4, md: 5 },
            mb: { xs: 4, md: 5 },
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(15px)",
            WebkitBackdropFilter: "blur(15px)",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h3" // Increased from h4 to h3
            component="h2"
            align="center"
            gutterBottom
            sx={{ mb: 4 }}
          >
            Take a Look
          </Typography>
          <Grid container spacing={3} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                <video
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  controls
                >
                  <source src="/Book1Trailer.mp4" title="Book Trailer" type="video/mp4" />
                </video>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 1,
                  color: "text.secondary",
                  fontStyle: "italic",
                  fontSize: "1rem", // Increased font size
                }}
              >
                Watch the Trailer
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                <video
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  controls
                >
                  <source src="/SHARD AWAKENING Video 2 Pre Luanch.mp4" title="Book Trailer" type="video/mp4" />
                </video>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 1,
                  color: "text.secondary",
                  fontStyle: "italic",
                  fontSize: "1rem", // Increased font size
                }}
              >
                Trailer 2
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <SignUpForm />
        </Box>
        <Modal
              open={openPreorderModal}
              onClose={handleClose}
              aria-labelledby="book-platforms-modal-title"
            >
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 500,
                maxHeight: '80vh',
                backgroundColor: '#ffffffff',
                color:"black",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                overflow: 'auto',
              }}>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <Close />
                </IconButton>
                
                <Typography id="book-platforms-modal-title" variant="h5" component="h2" sx={{ mb: 3, pr: 4 }}>
                  Get the Book
                </Typography>
                
                {/* Book Stores Section */}
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  Buy Physical & Digital Books
                </Typography>
                <List dense>
                  {bookPlatforms.map((platform) => (
                    <ListItem
                      key={platform.name}
                      component="div"
                      onClick={() => handlePlatformClick(platform.url)}
                      sx={{
                        borderRadius: 3, // Increased for more rounded corners
                        cursor: 'pointer',
                        padding: 2.5, // Increased padding for better spacing
                        margin: 1, // Add margin between items
                        background: `linear-gradient(135deg, ${platform.color} 0%, transparent 70%)`,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)', // Subtle lift effect on hover
                          boxShadow: 2, // Add shadow on hover
                        },
                        transition: 'all 0.2s ease-in-out', // Smooth transitions
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 56, // Increased spacing from icon
                        marginRight: 2 // Additional right margin
                      }}>
                        {platform.logo}
                      </ListItemIcon>
                      <ListItemText
                        primary={platform.name}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: 600, // Bolder text
                            fontSize: '1.25rem', // Larger font size (20px)
                            letterSpacing: '0.02em', // Slight letter spacing for readability
                            color: 'text.primary', // Ensure good contrast
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
        </Modal>
      </Container>
    </>
  );
};

export default ShardSeriesBook1;
